import { SlMath } from '../../../common/sl/sl-math';
import { SlBrand } from '../../../common/sl/sl-brand';

import * as ext from '@app/extensions';
import { _tr } from '@app/extensions';
import { FieldType } from './field-type.enum';
import { environment } from '@env/environment';

export class FieldWrapperModel {
  isVisible = false;
  isActive = false;

  private width = 86;
  private height = 36;
  private name = '';

  private brand = environment.brand;

  private normalSelectedItemBrush = SlBrand.colors[this.brand].bcg;
  private missedSelectedItemBrush = SlBrand.colors[this.brand].bcgErr;

  private normalArrowBrush = SlBrand.colors[this.brand].fill;
  private focusedArrowBrush = '#FFFFFF';
  private missedArrowBrush = SlBrand.colors[this.brand].fillErr;

  private normalButtonBrush = SlBrand.colors[this.brand].bcg;
  private focusedButtonBrush = SlBrand.colors[this.brand].fill;
  private missedButtonBrush = SlBrand.colors[this.brand].fillErr;
  private missedFocusedButtonBrush = SlBrand.colors[this.brand].fillErr;

  // signature and initial bcg
  private normalBackgroundBrush = SlBrand.colors[this.brand].bgcSignatureAndInitial;
  private missedBackgroundBrush = SlBrand.colors[this.brand].bcgErr;

  // signature and initial borders
  private normalBorderBrush = SlBrand.colors[this.brand].progress;
  private missedBorderBrush = SlBrand.colors[this.brand].fillErr;
  private bgcAreaIconBrush = SlBrand.colors[this.brand].bgcArrow;

  private normalBorderSize = 0;
  private focusedBorderSize = 0;

  private fontSize = 7;

  private signatureProcessingTitle = _tr('Click here to sign');
  private initialsProcessingTitle = _tr('Click here to initial');

  private x;
  private y;

  constructor(
    private document,
    private role,
    private field,
    private capitalizePipe,
    private fieldNamePipe,
    private info?,
  ) {
    this.init();
  }

  // Get
  get documentId() {
    if (!this.document) {
      return null;
    }

    return this.document.id;
  }

  get documentName() {
    if (!this.document) {
      return null;
    }

    return this.document.name;
  }

  get parentId() {
    return this.field.parentId;
  }

  get isSigned(): boolean {
    if (!this.field) {
      return false;
    }

    return !this.isValueEmpty(this.field.value);
  }

  get isFocused(): boolean {
    if (!this.field) {
      return false;
    }

    return this.field.isFocused;
  }

  get isAutoComplete(): boolean {
    return this.field.isAutoComplete;
  }

  get isRequired(): boolean {
    return this.field.isRequired;
  }

  get isMissed(): boolean {
    return this.field.isMissed;
  }

  get currentField(): any {
    return this.field;
  }

  get getOptionsLength(): number {
    if (!this.field) {
      return 0;
    }

    if (!this.field.options) {
      return 0;
    }

    if (!this.field.options.items) {
      return 0;
    }

    return this.field.options.items.length;
  }

  get id() {
    return this.field.id;
  }

  getOptionsItems() {
    if (!this.field) {
      return null;
    }

    return this.field.options && this.field.options.items;
  }

  getOptions() {
    if (!this.field) {
      return null;
    }

    return this.field.options;
  }

  getField() {
    return this.field;
  }

  getPageIndex() {
    return this.field.pageIndex;
  }

  get type() {
    if (!this.field) {
      return null;
    }

    const type = this.field.fieldType;

    if (type !== FieldType.Enum.TextField) {
      return type;
    }

    return this.field.options && this.field.options.isMultiline ? FieldType.Enum.TextArea : FieldType.Enum.TextField;
  }

  get value() {
    if (!this.field) {
      return null;
    }

    return this.field.value;
  }

  get defaultValue() {
    if (!this.field) {
      return null;
    }

    return this.field.defaultValue;
  }

  backgroundBrush(brand = 'LuluSoft') {
    return this.field.isMissed ? '#EC1C2419' : SlBrand.colors[brand].bgcSignatureAndInitial;
  }

  backgroundAreaIconBrush(brand) {
    return this.field.isMissed ? '#EC1C24' : SlBrand.colors[brand].bgcArrow;
  }

  get textColor() {
    return this.field.isMissed ? '#ffffff' : '#000000B2';
  }

  get borderBrush() {
    // return this.field.isMissed ? this.missedBorderBrush : this.normalBorderBrush;
    return null;
  }

  get checkboxAndRadioBorder(): string {
    return this.normalBorderBrush;
  }

  getBorderSize() {
    return this.field.isFocused ? this.focusedBorderSize : this.normalBorderSize;
  }

  getFontSize() {
    return this.fontSize;
  }

  getName() {
    return this.name;
  }

  getRect() {
    return SlMath.rect(this.field.left, this.field.top, this.field.left + this.field.width, this.field.top + this.field.height);
  }

  getStyle() {
    return {}; // TODO
  }

  getRadioGroup() {
    return this.field && this.field.options && this.field.options.radioGroup;
  }

  getSelectedGroupId() {
    return this.field.selectedGroupId;
  }

  isValueEmpty(value) {
    return ext.isUndefinedOrNull(value) || value === '';
  }

  // Set
  setSelectedGroupId(fieldId) {
    this.field.selectedGroupId = fieldId;
  }

  setValue(value) {
    if (value === 0) {
      value = 1;
    }

    if (!this.field) {
      return;
    }

    this.refreshInfo(value);
    this.field.value = value;
  }

  setFocus(value) {
    this.field.isFocused = value;
  }

  setMissed(value = true) {
    this.field.isMissed = value;
  }

  rename() {
    this.name = this.prepareName();
  }

  // Brush
  getArrowBrush(isOpen) {
    return isOpen ? this.focusedArrowBrush : this.isMissed ? this.missedArrowBrush : this.normalArrowBrush;
  }

  get selectedItemBrush() {
    return this.isMissed ? this.missedSelectedItemBrush : this.normalSelectedItemBrush;
  }

  getButtonBrush(isOpen) {
    return isOpen
      ? this.isMissed
        ? this.missedFocusedButtonBrush
        : this.focusedButtonBrush
      : this.isMissed
        ? this.missedButtonBrush
        : this.normalButtonBrush;
  }

  private refreshInfo(value) {
    if (this.isAutoComplete) {
      return;
    }
    if (this.isValueEmpty(this.field.value) && !this.isValueEmpty(value)) {
      this.info.count--;

      setTimeout(() => {
        this.field.isMissed = false;
      }, 300); // TODO temp
    } else if (this.isValueEmpty(value) && !this.isValueEmpty(this.field.value)) {
      this.info.count++;
    }
    this.info.isComplited = this.info.count === 0;
  }

  private init() {
    if (this.isAutoComplete) {
      return;
    }
    this.name = this.fieldNamePipe.transform(this.field.fieldType);

    // p.1 SPES-100
    // bug render optional empty text/checkbox with default value
    // if (!this.field.isRequired && this.isValueEmpty(this.field.value)) {
    //    this.setValue(null);
    // }

    if (!this.info) {
      return;
    }
    if (this.isValueEmpty(this.field.value)) {
      this.info.count++;
    }
    this.info.isComplited = this.info.count === 0;
  }

  private prepareName() {
    if (this.field.fieldType === FieldType.Enum.Signature) {
      return this.signatureProcessingTitle;
    } else if (this.field.fieldType === FieldType.Enum.Initials) {
      return this.initialsProcessingTitle;
    }

    return this.fieldNamePipe.transform(this.field.fieldType);
  }
}
